// apps/web/src/app/store/jaga.ts
/**
 * The forms holding unsaved changes, for the one question the shell asks before leaving
 * (PenjagaPerubahan): each edit form registers while it holds changes (useJagaPerubahan), a
 * save or Batal lets the next navigation through (`lewati`: for a moment, and only until a form
 * newly holds changes), and a step's own control can ask the same question outside a
 * navigation (`tanya`, answered through `jawab`).
 *
 * A form registers with what Tinggalkan does to it (`buang`): a screen that outlives the move
 * (the person page going to another person, a step to another member) drops its changes and
 * leaves edit mode, as the screens' own questions did before. A screen that saves field by
 * field (Isi angka paroki) registers a check instead (`pasangPeriksa`), asked at the moment
 * of the move: only what no save carries is lost. When the app reloads the tab itself (a tab
 * that takes the database back for another person, the wipe), `lepasPenjagaTab` lets the
 * reload through: the browser's own question must never stand in front of those.
 *
 * No React here and relative imports only: `auth/app.ts` and `auth/wipe.ts` call
 * `lepasPenjagaTab` from the launch path and the node tests.
 */
import { create } from 'zustand'
import { LEWATI_MS } from './jagaAturan'

export interface JagaState {
  /** Forms with unsaved changes. */
  aktif: Readonly<Record<string, true>>
  /** Screens that save field by field: does leaving now lose something? */
  periksa: Readonly<Record<string, () => boolean>>
  /** Until this instant (ms) the next navigation passes. */
  lewatiSampai: number
  /** What Tinggalkan does for a question asked outside a navigation. */
  permintaan: (() => void) | null
  /** The app reloads the tab itself: a closing tab is not held. */
  tabDilepas: boolean
  pasang(id: string, buang?: () => void): void
  lepas(id: string): void
  pasangPeriksa(id: string, hilang: () => boolean): void
  lepasPeriksa(id: string): void
  lewati(): void
  pakaiLewati(): void
  /** Tinggalkan: every registered form drops its changes. */
  buangSemua(): void
  tanya(lanjut: () => void): void
  jawab(tinggalkan: boolean): void
}

/** What Tinggalkan does to each registered form, by id (functions, not state to render). */
const pembuang = new Map<string, () => void>()

function tanpa<T>(rekaman: Readonly<Record<string, T>>, id: string): Record<string, T> {
  return Object.fromEntries(Object.entries(rekaman).filter(([k]) => k !== id))
}

/** The field-by-field screens that would lose something if the page went now. */
export function jumlahHilang(s: Pick<JagaState, 'periksa'>): number {
  let jumlah = 0
  for (const hilang of Object.values(s.periksa)) if (hilang()) jumlah += 1
  return jumlah
}

/** The forms that would lose something if the page went now: what leaving asks about. */
export function jumlahKotor(s: Pick<JagaState, 'aktif' | 'periksa'>): number {
  return Object.keys(s.aktif).length + jumlahHilang(s)
}

export const useJaga = create<JagaState>()((set, get) => ({
  aktif: {},
  periksa: {},
  lewatiSampai: 0,
  permintaan: null,
  tabDilepas: false,
  // A form that newly holds changes ends any pass a save left unused (a save that stayed on
  // its page): the pass was for the move right after that save, not for these changes.
  pasang: (id, buang) => {
    if (buang === undefined) pembuang.delete(id)
    else pembuang.set(id, buang)
    set((s) => (s.aktif[id] === true ? s : { aktif: { ...s.aktif, [id]: true }, lewatiSampai: 0 }))
  },
  lepas: (id) => {
    pembuang.delete(id)
    set((s) => (s.aktif[id] === true ? { aktif: tanpa(s.aktif, id) } : s))
  },
  pasangPeriksa: (id, hilang) => set((s) => ({ periksa: { ...s.periksa, [id]: hilang } })),
  lepasPeriksa: (id) => set((s) => (id in s.periksa ? { periksa: tanpa(s.periksa, id) } : s)),
  lewati: () => set({ lewatiSampai: Date.now() + LEWATI_MS }),
  pakaiLewati: () => set({ lewatiSampai: 0 }),
  buangSemua: () => {
    for (const buang of [...pembuang.values()]) buang()
  },
  tanya: (lanjut) => {
    if (jumlahKotor(get()) === 0) lanjut()
    else set({ permintaan: lanjut })
  },
  jawab: (tinggalkan) => {
    const lanjut = get().permintaan
    set({ permintaan: null })
    if (tinggalkan && lanjut !== null) {
      // A form whose reset fails does not stop what Tinggalkan was asked to do.
      try {
        get().buangSemua()
      } finally {
        get().lewati()
        lanjut()
      }
    }
  },
}))

/** A save that navigates on its own (a wizard's Lanjut) lets that navigation through. */
export function lewatiPenjaga(): void {
  useJaga.getState().lewati()
}

/**
 * The app is about to reload the tab itself (a tab taking the database back, the wipe): the
 * browser's "leave site?" question must not hold it. Call it on the line before the reload.
 */
export function lepasPenjagaTab(): void {
  useJaga.setState({ tabDilepas: true })
}
